// React and state
import { useEffect, useState } from "react";

// UI components
import { Box, Typography, TextField, Button, Snackbar } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Styles
import email_card_styles from "./email_card_styles";

// Utilities and services
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "../../../services/StoreHelper";
import { update_state_value } from "../../../store/reducers/AppReducer";
import { get_token } from "../../../services/ApiHelper";
import { login } from "../../../services/api.calls.services";

// Email validation schema
const emailSchema = z.string().email();

function EmailCard() {
  const dispatch = useAppDispatch();
  const { app_dimensions, club } = useAppSelector((state) => state.app);
  const styles = email_card_styles(app_dimensions);

  // View and input state
  const [view, setView] = useState("1");
  const [email, setEmail] = useState("");

  // Auth/token state
  const [token, setToken] = useState("");

  // Feedback state
  const [message, setMessage] = useState("");
  const [resendMsg, setResendMsg] = useState(
    "Link has been resent to your email."
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);

  useEffect(() => {
    get_token().then((data) => setToken(`Bearer ${data}`));
  }, []);

  const validateEmail = () => emailSchema.safeParse(email).success;

  const getHelperText = () =>
    !validateEmail() ? "Please enter a valid email address." : message || "";

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
    setMessage("");
    setSnackbarOpen(false);
  };

  const handleCloseSnackbar = (_: any, reason: string) => {
    if (reason !== "clickaway") setResendOpen(false);
  };

  const handleSend = (isResend = false) => {
    if (!validateEmail()) return;

    login(token, club.clubId, email)
      .then(() => {
        if (isResend) {
          setResendMsg("A new login link has been sent to your email.");
          setResendOpen(true);
        } else {
          setView("2");
        }
      })
      .catch(({ response }) => {
        const msg = response.data;
        setMessage(msg);
        setSnackbarOpen(true);
        if (isResend) {
          setResendMsg(msg);
          setResendOpen(true);
        }
      });
  };

  return (
    <div style={{ padding: 0 }}>
      {view === "1" && (
        <div style={styles.body}>
          <div style={styles.card_head}>
            <div style={styles.headline}>
              <h1 style={styles.card_title}>Manage My Agreements</h1>
              <h2 style={styles.card_subtitle}>
                Please enter the email address used to purchase your agreement.
              </h2>
            </div>
          </div>

          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <Typography
              variant="subtitle2"
              component="label"
              htmlFor="email-input"
              sx={{ textAlign: "left" }}
            >
              Email
            </Typography>
            <TextField
              id="email-input"
              variant="outlined"
              fullWidth
              value={email}
              onChange={handleEmailChange}
              error={!validateEmail() || !!message}
              helperText={getHelperText()}
            />
            <button
              style={validateEmail() ? styles.button : styles.button_disabled}
              onClick={() => handleSend()}
              disabled={!validateEmail()}
            >
              Continue
            </button>
          </Box>
        </div>
      )}

      {view === "2" && (
        <div style={styles.body_2}>
          <Button
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              fontFamily: "Montserrat",
              textTransform: "none",
            }}
            onClick={() => {
              setView("1");
              setMessage("");
            }}
          >
            <div style={styles.back_btn_contain}>
              <ArrowBackIcon style={{ color: "black" }} />
              <div style={styles.back_btn}>
                <p style={styles.back_btn_text}>Back</p>
              </div>
            </div>
          </Button>

          <div style={styles.icon_contain}>
            <img
              src="/link_icon.svg"
              style={styles.icon}
              width={64}
              height={62}
            />
          </div>

          <div style={styles.confirm_head}>
            <div style={styles.confirm_headline}>
              <h1 style={styles.confirm_title}>Check Your Email</h1>
              <h6 style={styles.confirm_subtitle}>
                If your email address below was located, an email has been sent
                with a link to manage your agreements:
              </h6>
              <h6 style={styles.confirm_email}>{email}</h6>

              <Button
                style={styles.resend_button}
                onClick={() => handleSend(true)}
              >
                Resend email
              </Button>

              <Snackbar
                open={resendOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message={resendMsg}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailCard;